/**
 * A platform is a known category of devices the player will be used on.
 *
 * @remarks
 * The primary reason for the platform is to determine the correct player type.
 * Some devices have known performance issues with some player technologies,
 * and we use the platform to determine the most suitable player type accordingly.
 *
 * The second reason is to call the correct remote config endpoint.
 * We internally map the platform value to a value known to our remote config service.
 * The list of platforms known to our remote config service is limited, and not as granular as the platform enum,
 * but we need the extra granularity for other purposes like platform specific tweaks.
 *
 * The third reason is for tracking purposes.
 * We send the platform value to our analytics service and to our error reporting.
 *
 * We automatically detect the platform for most devices, but you can override the value if you need to.
 * This may be the case if an automatic detection is not possible like for some Vodafone devices.
 * If you do not know the platform for sure, you should not set it.
 *
 * In the future we may also use the platform to enable or disable certain features or tweaks.
 * Right now we additionally run the same check, which we use to detect the platform, to enable tweaks.
 * This is not ideal, because we run the same check twice, and we may run into issues if we want to enable a tweak for a platform,
 * but our detection logic does not detect the platform correctly.
 *
 * !! If you add items here, you might need to also add them to @see ../../../players/core/src/player-config.ts
 */
export const Platform = {
  /**
   * Web: Desktops, notebooks, smartphones and tablets.
   *
   * @remarks
   * This platform is the default platform, if we cannot detect a more specific platform.
   *
   * This setting is very generic and does not inherently apply any specific tweaks.
   */
  Web: 'web',

  /**
   * LG WebOS 3.x (2016-2017)
   *
   * @remarks
   * The browser on this platform is based on Chromium 38 according to LG.
   * This is also the first version based on Chromium.
   *
   * We currently do not support this platform, but we may do so in the future.
   *
   * @see https://webostv.developer.lge.com/develop/specifications/web-api-and-web-engine
   */
  WebOS3: 'webos-3.x',

  /**
   * LG WebOS 4.x (2018-2019)
   *
   * @remarks
   * The browser on this platform is based on Chromium 53 according to LG.
   *
   * This is the oldest generation of WebOS we currently support.
   *
   * LG still actively sells devices with this version of WebOS.
   *
   * @see https://webostv.developer.lge.com/develop/specifications/web-api-and-web-engine
   */
  WebOS4: 'webos-4.x',

  /**
   * LG WebOS 5.x (2020)
   *
   * @remarks
   * The browser on this platform is based on Chromium 68 according to LG.
   *
   * @see https://webostv.developer.lge.com/develop/specifications/web-api-and-web-engine
   */
  WebOS5: 'webos-5.x',

  /**
   * LG WebOS 6.x (2021)
   *
   * @remarks
   * The browser on this platform is based on Chromium 79 according to LG.
   *
   * @see https://webostv.developer.lge.com/develop/specifications/web-api-and-web-engine
   */
  WebOS6: 'webos-6.x',

  /**
   * LG WebOS 22 (2022)
   *
   * @remarks
   * The browser on this platform is based on Chromium 87 according to LG.
   *
   * Devices with WebOS 22 may also show up as WebOS 7.x, but LG specifies this version as 22 on their website.
   *
   * @see https://webostv.developer.lge.com/develop/specifications/web-api-and-web-engine
   */
  WebOS22: 'webos-22',

  /**
   * LG WebOS 23 (2023)
   *
   * @remarks
   * The browser on this platform is based on Chromium 94 according to LG.
   *
   * Devices with WebOS 23 may also show up as WebOS 8.x, but LG specifies this version as 23 on their website.
   *
   * @see https://webostv.developer.lge.com/develop/specifications/web-api-and-web-engine
   */
  WebOS23: 'webos-23',

  /**
   * LG WebOS 24 (2024)
   *
   * @remarks
   * The browser on this platform is based on Chromium 108 according to LG.
   *
   * Devices with WebOS 24 may also show up as WebOS 9.x, but LG specifies this version as 24 on their website.
   *
   * @see https://webostv.developer.lge.com/develop/specifications/web-api-and-web-engine
   */
  WebOS24: 'webos-24',

  /**
   * LG WebOS 25 (2025)
   *
   * @remarks
   * The browser on this platform is based on Chromium 120 according to LG.
   *
   * Devices with WebOS 25 may also show up as WebOS 10.x, but LG specifies this version as 25 on their website.
   *
   * @see https://webostv.developer.lge.com/develop/specifications/web-api-and-web-engine
   */
  WebOS25: 'webos-25',

  /**
   * Tizen 3.x (2017)
   *
   * @remarks
   * The browser on this platform is based on Chromium 47 according to Samsung.
   *
   * This is the oldest generation of Tizen we currently support.
   * This is also the generation with the most known issues.
   * In general if the player works on this platform, it will work on newer versions of Tizen as well.
   *
   * @see https://developer.samsung.com/smarttv/develop/specifications/web-engine-specifications.html
   */
  Tizen3: 'tizen-3.0',

  /**
   * Tizen 4.x (2018)
   *
   * @remarks
   * The browser on this platform is based on Chromium 56 according to Samsung.
   *
   * @see https://developer.samsung.com/smarttv/develop/specifications/web-engine-specifications.html
   */
  Tizen4: 'tizen-4.0',

  /**
   * Tizen 5.x (2019)
   *
   * @remarks
   * The browser on this platform is based on Chromium 63 according to Samsung.
   *
   * @see https://developer.samsung.com/smarttv/develop/specifications/web-engine-specifications.html
   */
  Tizen5: 'tizen-5.0',

  /**
   * Tizen 5.5 (2020)
   *
   * @remarks
   * The browser on this platform is based on Chromium 69 according to Samsung.
   *
   * Beware that Tizen widgets for this versions act differently than for the versions prior to this one.
   * The widget manifest validation is stricter and the player may not work as expected.
   * Newer generations use the same validation.
   *
   * @see https://developer.samsung.com/smarttv/develop/specifications/web-engine-specifications.html
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Tizen5_5: 'tizen-5.5',

  /**
   * Tizen 6.x (2021)
   *
   * @remarks
   * The browser on this platform is based on Chromium 76 according to Samsung.
   *
   * @see https://developer.samsung.com/smarttv/develop/specifications/web-engine-specifications.html
   */
  Tizen6: 'tizen-6.0',

  /**
   * Tizen 6.5 (2022)
   *
   * @remarks
   * The browser on this platform is based on Chromium 85 according to Samsung.
   *
   * @see https://developer.samsung.com/smarttv/develop/specifications/web-engine-specifications.html
   */
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Tizen6_5: 'tizen-6.5',

  /**
   * Tizen 7.x (2023)
   *
   * @remarks
   * The browser on this platform is based on Chromium 94 according to Samsung.
   *
   * @see https://developer.samsung.com/smarttv/develop/specifications/web-engine-specifications.html
   *
   * @privateRemarks
   * The value should be 7.0 for consistency, but we keep it this way for 1.x compatibility.
   */
  Tizen7: 'tizen-7',

  /**
   * Tizen 8.x (2024)
   *
   * @remarks
   * The browser on this platform is based on Chromium 108 according to Samsung.
   *
   * @see https://developer.samsung.com/smarttv/develop/specifications/web-engine-specifications.html
   */
  Tizen8: 'tizen-8.0',

  /**
   * Tizen 9.x (2024)
   *
   * @remarks
   * Sadly the exact chrome version is not yet mentioned in the samsung documentation
   */
  Tizen9: 'tizen-9.0',

  /**
   * Sky Q, also known as Amidala or ES160
   *
   * @remarks
   * Sky has three different devices. The big one, a normal set-top-box format, is the Sky Q.
   *
   * We do not know the exact version of the browser on this platform.
   * According to the user agent it seems to be based on Chromium 56 or similar.
   *
   * We force the dash.js player on this platform, because the Bitmovin player has a multitude of issues,
   * ranging from excessive buffering, memory leaks, to not playing at all.
   *
   * @see https://www.sky.de/produkte/sky-q
   */
  SkyQ: 'skyq',

  /**
   * Sky XiOne
   *
   * @remarks
   * This may be also be called Xi1 or Q IPTV Box.
   *
   * Sky has three different devices. The small one and two slim square boxes, these are the Sky Stream and
   * XiOne. From the outside, they aren't distinguishable, just trough details. Like the small button
   * at the bottom showing an on-off sign for the Sky Stream and two arrows on the XiOne.
   *
   * We do not know the exact version of the browser on this platform.
   * According to the user agent it seems to be based on WebKit, WPE specifically, in version 601.1.
   *
   * This platform does not force dash.js, because it seems to work fine with the Bitmovin player.
   *
   * Even though this is also a Sky device, even called Sky Q IPTV according to Sky,
   * this platform is fundamentally different from the Sky Q.
   *
   * @see https://www.sky.de/produkte/sky-q
   */
  SkyXiOne: 'skyxione',

  /**
   * Sky Stream
   *
   * @remarks
   * Sky has three different devices. The small one and two slim square boxes, these are the Sky Stream and
   * XiOne. From the outside, they aren't distinguishable, just trough details. Like the small button
   * at the bottom showing an on-off sign for the Sky Stream and two arrows on the XiOne.
   */
  SkyStream: 'skystream',

  /**
   * Magenta G3
   *
   * @remarks
   * Also referred to as Magenta G3.9. It is the oldest Telekom Set-Top-Box.
   * They have the same form factor and design as the Magenta G4.
   * The device designations are usually MR200 and MR400.
   *
   * We do not support this generation of devices. They have atrocious performance and spec compliance.
   * Spec compliance is so bad, that these devices do not even support every feature of ES5.
   */
  MagentaG3: 'magenta-g3',

  /**
   * Magenta G4
   *
   * @remarks
   * They have the same form factor and design as the Magenta G3.9.
   * The device designations are usually MR201, MR401 and MR601.
   *
   * The devices are fundamentally different from the Magenta G3.9.
   * The browser is roughly equal to a Safari 11.1. According to the specs it is based on WebKit.
   *
   * This is the oldest generation of Magenta devices we currently support.
   *
   * We force the dash.js player on this platform, because the Bitmovin player has a multitude of issues,
   * ranging from excessive buffering to not playing at all.
   * The Telekom also suggests to use the dash.js player on this platform.
   */
  MagentaG4: 'magenta-g4',

  /**
   * Magenta G5
   *
   * @remarks
   * They look distinctly different in comparison to the G4 devices.
   * The device designations are usually MRG5.
   *
   * G5 devices come in two form factors. A small square box and a slim box.
   * The slim G5 devices do not have a separate hard drive slot, but also perform worse.
   * According to Telekom this should not be the case, but we have observed this in practice.
   *
   * The browser is roughly equal to a Safari 11.1. According to the specs it is based on WebKit.
   * The G4 and G5 devices use the same browser, but the G5 devices have better hardware and thus perform better.
   *
   * We force the dash.js player on this platform, because the Bitmovin player has a multitude of issues,
   * ranging from excessive buffering to not playing at all.
   * The Telekom also suggests to use the dash.js player on this platform.
   */
  MagentaG5: 'magenta-g5',

  /**
   * Vodafone GTV
   *
   * @remarks
   * This is the oldest generation of Vodafone devices we currently support.
   *
   * The browser is roughly similar to a Safari 9 according to our testing.
   *
   * This platform does not support a lot of DOM features and is does thus use Canvas for rendering.
   * This also limits our abilities to debug issues on this platform and what features we can support.
   *
   * One of the most notable changes is that we do not use two video elements on this platform,
   * but instead force ads to play in the same video element as the content.
   *
   * We force the dash.js player on this platform, because the Bitmovin player does not even start properly most of the time.
   */
  VodafoneGTV: 'vodafone-gtv',

  /**
   * Vodafone VTV
   *
   * @remarks
   * The browser is roughly similar to Opera 36 according to our testing and communication with Vodafone.
   *
   * The devices are fundamentally different from the Vodafone GTV.
   * Unlike the Vodafone GTV, this platform supports normal DOM features.
   *
   * We force the dash.js player on this platform, because the dash.js player has better performance on this platform.
   */
  VodafoneVTV: 'vodafone-vtv',

  /**
   * Chromecast
   *
   * @remarks
   * This is meant for second generation Chromecasts and newer as well as Chrome with Google TV.
   * In general this should be explicitly set, because the detection is not always reliable.
   *
   * We force a custom player on this platform, which will use Chromecast CAF v3 for the playback.
   */
  Chromecast: 'chromecast',

  /**
   * TeleColumbus PYUR
   *
   * @remarks
   * TeleColumbus is an Android TV set-top-box, but RTL+ uses a HTML5 app on it.
   * The deployed Android app integrates modern Chrome browser to facilitate the HTML5 app.
   */
  TeleColumbus: 'telecolumbus',

  /**
   * Amazon Kepler
   *
   * @remarks
   * Kepler uses React Native and is a new platform from Amazon.
   *
   * We only support the WebView mode for Kepler.
   */
  Kepler: 'kepler',
} as const;

/**
 * {@inheritDoc Platform:var}
 */
export type Platform = (typeof Platform)[keyof typeof Platform];
